import React, { useCallback, useEffect, useRef } from 'react';

import { Transition } from 'react-transition-group';

import {
  defaultTimeout,
  resizeDebounceMs,
  transitionEasing,
} from '@core/transitions/constants';
import { Fade } from '@core/transitions/Fade';
import { useReducedMotion } from '@core/transitions/hooks/useReducedMotion';
import type { SlideDirection } from '@core/transitions/types';
import {
  getTransitionTimeout,
  setTranslateValue,
} from '@core/transitions/utils';
import type { TransitionTimeout } from '@core/transitions/utils';
import { reflow, useForkRef } from '@core/utils';

type ChildWithRef = React.ReactElement & { ref?: React.Ref<unknown> };

export type Props = {
  direction?: SlideDirection;
  /** When true, the child is visible (entered). Default false. */
  in?: boolean;
  /** Single element to slide. When undefined, nothing is rendered. */
  children?: React.ReactElement;
  appear?: boolean;
  mountOnEnter?: boolean;
  unmountOnExit?: boolean;
  /** Duration in ms, or { enter, exit }. Uses DURATION.normal / DURATION.fast when omitted. */
  timeout?: TransitionTimeout;
  style?: React.CSSProperties;
  /** Called when enter starts. With nodeRef, Slide calls this with (nodeRef.current, isAppearing). */
  onEnter?: (node: HTMLElement, isAppearing: boolean) => void;
  /** Called when exit has finished. With nodeRef, Slide calls this with (nodeRef.current). */
  onExited?: (node: HTMLElement) => void;
};

const Slide = (
  props: Props,
  ref: React.Ref<HTMLElement>
): React.ReactElement | null => {
  const {
    appear = true,
    children,
    direction = 'down',
    in: inProp = false,
    mountOnEnter = false,
    onEnter: onEnterProp,
    onExited: onExitedProp,
    unmountOnExit = false,
    style: styleProp,
    timeout = defaultTimeout,
  } = props;

  const reducedMotion = useReducedMotion();
  const nodeRef = useRef<HTMLElement | null>(null);

  const enterDuration = getTransitionTimeout(timeout, 'enter', defaultTimeout);
  const exitDuration = getTransitionTimeout(timeout, 'exit', defaultTimeout);
  const timeoutProp =
    typeof timeout === 'object'
      ? { enter: enterDuration, exit: exitDuration }
      : timeout;

  const handleOwnRef = useCallback(
    (value: React.ReactInstance | HTMLElement | null) => {
      nodeRef.current = value instanceof HTMLElement ? value : null;
    },
    []
  );
  const handleRef = useForkRef(
    useForkRef((children as ChildWithRef).ref, handleOwnRef),
    ref
  );

  const handleEnter = useCallback(
    (isAppearing?: boolean) => {
      const node = nodeRef.current;
      if (node) {
        setTranslateValue(direction, node);
        reflow(node);
      }
      if (node) onEnterProp?.(node, isAppearing ?? false);
    },
    [direction, onEnterProp]
  );

  const handleEntering = useCallback(() => {
    const node = nodeRef.current;
    if (node) {
      node.style.transition = `transform ${enterDuration}ms ${transitionEasing.entrance}`;
      node.style.transform = 'none';
    }
  }, [enterDuration]);

  const handleExit = useCallback(() => {
    const node = nodeRef.current;
    if (node) {
      node.style.transition = `transform ${exitDuration}ms ${transitionEasing.exit}`;
      setTranslateValue(direction, node);
    }
  }, [direction, exitDuration]);

  const handleExited = useCallback(() => {
    const node = nodeRef.current;
    if (node) {
      node.style.transition = '';
    }
    if (node) onExitedProp?.(node);
  }, [onExitedProp]);

  const updatePosition = useCallback(() => {
    if (nodeRef.current) {
      setTranslateValue(direction, nodeRef.current);
    }
  }, [direction]);

  useEffect(() => {
    if (inProp || direction === 'down' || direction === 'right') {
      return undefined;
    }
    let resizeTimeout: ReturnType<typeof setTimeout> | null = null;
    const handleResize = () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        resizeTimeout = null;
        if (nodeRef.current) setTranslateValue(direction, nodeRef.current);
      }, resizeDebounceMs);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
    };
  }, [direction, inProp]);

  useEffect(() => {
    if (!inProp) {
      updatePosition();
    }
  }, [inProp, updatePosition]);

  if (children == null) return null;

  if (reducedMotion) {
    return (
      <Fade
        ref={ref}
        appear={appear}
        in={inProp}
        mountOnEnter={mountOnEnter}
        style={styleProp}
        timeout={timeout}
        unmountOnExit={unmountOnExit}
        onEnter={onEnterProp}
        onExited={onExitedProp}
      >
        {children}
      </Fade>
    );
  }

  return (
    <Transition
      appear={appear}
      in={inProp}
      mountOnEnter={mountOnEnter}
      nodeRef={nodeRef as React.RefObject<HTMLElement>}
      timeout={timeoutProp}
      unmountOnExit={unmountOnExit}
      onEnter={handleEnter}
      onEntering={handleEntering}
      onExit={handleExit}
      onExited={handleExited}
    >
      {(state) =>
        React.cloneElement(children, {
          ref: handleRef,
          style: {
            visibility: state === 'exited' && !inProp ? 'hidden' : undefined,
            ...styleProp,
            ...(children.props as { style?: React.CSSProperties }).style,
          },
        })
      }
    </Transition>
  );
};

export default React.forwardRef(Slide);
